import { useEffect, useState, useRef } from "react";
import { ref, onValue, push, off, get, set } from "firebase/database";
import { db } from "../../../configs/Firebase";
import { useSelector } from "react-redux";
import Icon from "../../../designSystem/a-atoms/icons/Icon";
import Button from "../../../designSystem/b-molecules/buttons/Button";
import TipTapCommentInput from "./TipTapCommentInput";
import style from "./CellCommentsSidebar.module.css";

// Helper function to render comment text with mention styling
function renderCommentWithMentions(text, mentionData, mentionIndices) {
  if (!text) return text;
  
  // If we have mention indices, use them for precise rendering
  if (mentionIndices && Array.isArray(mentionIndices) && mentionIndices.length > 0) {
    const parts = [];
    let lastIndex = 0;
    let key = 0;
    
    // Sort mentions by start index
    const sortedMentions = [...mentionIndices].sort((a, b) => a.start - b.start);
    
    sortedMentions.forEach((mention) => {
      // Add text before the mention
      if (mention.start > lastIndex) {
        parts.push(<span key={key++}>{text.substring(lastIndex, mention.start)}</span>);
      }
      
      // Add styled mention
      parts.push(
        <span key={key++} className={style.mentionDisplay}>
          {text.substring(mention.start, mention.end)}
        </span>
      );
      
      lastIndex = mention.end;
    });
    
    // Add remaining text after last mention
    if (lastIndex < text.length) {
      parts.push(<span key={key++}>{text.substring(lastIndex)}</span>);
    }
    
    return parts.length > 0 ? parts : text;
  }
  
  // Fallback: parse mentions from text (for backward compatibility)
  if (!mentionData || Object.keys(mentionData).length === 0) {
    return text;
  }
  
  const mentionRegex = /@(\w+(?:\s+\w+)*)/g;
  const parts = [];
  let lastIndex = 0;
  let match;
  let key = 0;
  
  while ((match = mentionRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(<span key={key++}>{text.substring(lastIndex, match.index)}</span>);
    }
    
    const mentionText = match[1].trim();
    const fullMention = match[0];
    
    const matchedUserId = Object.keys(mentionData).find(userId => {
      const displayName = (mentionData[userId] || "").toLowerCase();
      return displayName.includes(mentionText.toLowerCase());
    });
    
    if (matchedUserId) {
      parts.push(
        <span key={key++} className={style.mentionDisplay}>
          {fullMention}
        </span>
      );
    } else {
      parts.push(<span key={key++}>{fullMention}</span>);
    }
    
    lastIndex = match.index + fullMention.length;
  }
  
  if (lastIndex < text.length) {
    parts.push(<span key={key++}>{text.substring(lastIndex)}</span>);
  }
  
  return parts.length > 0 ? parts : text;
}

const CellCommentsSidebar = ({ show, onClose, journeyRefURL, rowId, cellId, user, journeyId, journeyName }) => {
  const org = useSelector((state) => state.orgStatus.value);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [newCommentMentions, setNewCommentMentions] = useState([]);

  useEffect(() => {
    if (!show || !journeyRefURL || !rowId || !cellId) {
      return;
    }

    const commentsRef = ref(
      db,
      `${journeyRefURL}/rows/${rowId}/columns/${cellId}/comments`
    );

    const unsubscribe = onValue(commentsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const commentsList = [];
        for (let i in data) {
          const timestamp = data[i].timestamp;
          const date = new Date(timestamp);
          const datevalues = [
            date.getFullYear(),
            date.getMonth() + 1,
            date.getDate(),
            date.getHours(),
            date.getMinutes(),
            date.getSeconds(),
          ];
          commentsList.push({
            id: i,
            ...data[i],
            timestampOrig: timestamp,
            timestamp: (
              <div className={style.commentDate}>
                <div>
                  {String(datevalues[3]).padStart(2, "0")}:
                  {String(datevalues[4]).padStart(2, "0")}:
                  {String(datevalues[5]).padStart(2, "0")}
                </div>
                <div>
                  {String(datevalues[2]).padStart(2, "0")} /{" "}
                  {String(datevalues[1]).padStart(2, "0")} / {datevalues[0]}
                </div>
              </div>
            ),
          });
        }
        // Sort by timestamp (most recent first)
        commentsList.sort((a, b) => {
          return b.timestampOrig - a.timestampOrig;
        });
        setComments(commentsList);
      } else {
        setComments([]);
      }
    });

    return () => {
      off(commentsRef);
      unsubscribe();
    };
  }, [show, journeyRefURL, rowId, cellId]);

  function newCommentChangeHandler(text) {
    setNewComment(text);
  }
  
  function newCommentMentionsChangeHandler(mentions) {
    setNewCommentMentions(mentions);
  }

  async function getUserIdFromMention(mentionText, orgId) {
    // Get users from org and find matching user using partial matching
    try {
      const usersRef = ref(db, `userData/${orgId}/users/`);
      const snapshot = await get(usersRef);
      const data = snapshot.val();
      
      if (data) {
        const mentionLower = mentionText.toLowerCase().trim();
        const matches = [];
        
        for (let userId in data) {
          if (data[userId].id) {
            const profileSnapshot = await get(ref(db, `userData/${data[userId].id}/profile`));
            const profile = profileSnapshot.val() || {};
            const displayName = (profile.displayName || "").toLowerCase();
            const email = (profile.email || "").toLowerCase();
            
            // Partial matching: check if displayName or email contains the mention text
            if (displayName && displayName.includes(mentionLower)) {
              matches.push({
                id: data[userId].id,
                displayName: profile.displayName || "",
                matchLength: displayName.length - mentionLower.length, // Prefer more specific matches
              });
            } else if (email && email.includes(mentionLower)) {
              matches.push({
                id: data[userId].id,
                displayName: profile.displayName || email,
                matchLength: email.length - mentionLower.length,
              });
            }
          }
        }
        
        // Return the most specific match (shortest matchLength) or first match
        if (matches.length > 0) {
          matches.sort((a, b) => a.matchLength - b.matchLength);
          return matches[0].id;
        }
      }
    } catch (error) {
      console.error("Error finding user for mention:", error);
    }
    return null;
  }

  async function createNotifications(mentionedUserIds, commentId, commentText) {
    if (!org.payload || !journeyId || !journeyName) return;
    
    // Get all users who have commented on this cell (for reply notifications)
    const commentsRef = ref(db, `${journeyRefURL}/rows/${rowId}/columns/${cellId}/comments`);
    const commentsSnapshot = await get(commentsRef);
    const existingComments = commentsSnapshot.val() || {};
    
    const threadParticipants = new Set();
    for (let commentId in existingComments) {
      if (existingComments[commentId].userId && existingComments[commentId].userId !== user.uid) {
        threadParticipants.add(existingComments[commentId].userId);
      }
    }
    
    // Create notifications for mentioned users
    for (const mentionedUserId of mentionedUserIds) {
      if (mentionedUserId !== user.uid) {
        const notificationRef = ref(db, `userData/${mentionedUserId}/notifications`);
        push(notificationRef, {
          type: "mention",
          journeyId: journeyId,
          journeyName: journeyName,
          rowId: rowId,
          cellId: cellId,
          commentId: commentId,
          fromUserId: user.uid,
          fromUserName: user.displayName,
          text: commentText.substring(0, 100), // Preview
          read: false,
          timestamp: { ".sv": "timestamp" },
        });
      }
    }
    
    // Create reply notifications for thread participants who weren't mentioned
    for (const participantId of threadParticipants) {
      if (!mentionedUserIds.includes(participantId) && participantId !== user.uid) {
        const notificationRef = ref(db, `userData/${participantId}/notifications`);
        push(notificationRef, {
          type: "reply",
          journeyId: journeyId,
          journeyName: journeyName,
          rowId: rowId,
          cellId: cellId,
          commentId: commentId,
          fromUserId: user.uid,
          fromUserName: user.displayName,
          text: commentText.substring(0, 100), // Preview
          read: false,
          timestamp: { ".sv": "timestamp" },
        });
      }
    }
  }

  async function addCommentHandler() {
    if (!user || !journeyRefURL || !rowId || !cellId || !org.payload) {
      return;
    }
    
    const text = newComment.trim();
    
    if (!text) {
      return;
    }

    // Extract mentions from the mentions array (already extracted by TipTap)
    const mentions = [];
    const mentionData = {};
    
    // newCommentMentions contains { userId, display, start, end }
    for (const mention of newCommentMentions) {
      if (mention.userId && !mentions.includes(mention.userId)) {
        mentions.push(mention.userId);
        mentionData[mention.userId] = mention.display;
      }
    }

    const commentsRef = ref(
      db,
      `${journeyRefURL}/rows/${rowId}/columns/${cellId}/comments`
    );
    
    const commentRef = push(commentsRef);
    const commentId = commentRef.key;
    
    await set(commentRef, {
      text: text,
      by: user.displayName,
      userId: user.uid,
      mentions: mentions,
      mentionData: mentionData,
      mentionIndices: newCommentMentions, // Store indices for display
      timestamp: { ".sv": "timestamp" },
    });
    
    // Create notifications
    await createNotifications(mentions, commentId, text);
    
    setNewComment("");
    setNewCommentMentions([]);
  }

  return (
    <div className={`${style.cellCommentsSidebar} ${show ? style.show : ""}`}>
      <div className={style.header}>
        <span>Comments</span>
        <Icon onClick={onClose}>close</Icon>
      </div>
      <div className={style.commentsList}>
        {comments.length === 0 ? (
          <div className={style.emptyComments}>
            <p>No comments yet.</p>
            <p>Be the first to add a comment!</p>
          </div>
        ) : (
          comments.map((comment) => (
            <div key={comment.id} className={style.commentItem}>
              <div className={style.commentHeader}>
                <div className={style.commentAuthor}>{comment.by}</div>
                <div className={style.commentTimestamp}>{comment.timestamp}</div>
              </div>
              <div className={style.commentText}>
                {renderCommentWithMentions(comment.text, comment.mentionData, comment.mentionIndices)}
              </div>
            </div>
          ))
        )}
      </div>
      <div className={style.addComment}>
        <div className={style.commentInputWrapper}>
          <TipTapCommentInput
            value={newComment}
            onChange={newCommentChangeHandler}
            onMentionsChange={newCommentMentionsChangeHandler}
            placeholder="Add a comment..."
            orgId={org.payload}
          />
        </div>
        <Button preIcon="send" onClick={addCommentHandler} variants={["text"]}>
          Send
        </Button>
      </div>
    </div>
  );
};

export default CellCommentsSidebar;

